import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { GEPlayerInsightsState, GEQuestions } from '../models/gePlayerInsights';
import request from '../utils/request';
import { RootState } from '../utils/store';
import { signOut } from './user';

export const fetchPlayerInsight = createAsyncThunk(
  'gePlayerInsights/fetchPlayerInsight',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('api/player-insight');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchPlayerInsightById = createAsyncThunk(
  'gePlayerInsights/fetchPlayerInsightById',
  async (payload: { sessionId: string; idSA: number }, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `api/player-insight?sessionId=${payload.sessionId}`
      );
      return { ...data, idSA: payload.idSA };
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchPlayerInsightBySessionId = createAsyncThunk(
  'gePlayerInsights/fetchPlayerInsightBySessionId',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `api/player-insight?sessionId=${sessionId}`
      );
      return { ...data };
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchGE = createAsyncThunk(
  'gePlayerInsights/fetchGE',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('api/game-evaluation');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchGERecent = createAsyncThunk(
  'gePlayerInsights/fetchGERecent',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('api/game-evaluation/recent');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchGEById = createAsyncThunk(
  'gePlayerInsights/fetchGEById',
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`api/game-evaluation/${id}`);
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchGEByIdNoneLoading = createAsyncThunk(
  'gePlayerInsights/fetchGESByIdNoneLoading',
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`api/game-evaluation/${id}`);
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchSALessonById = createAsyncThunk(
  'gePlayerInsights/fetchSALessonById',
  async (payload: { id: number; idSA: number }, { rejectWithValue }) => {
    try {
      const time = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await request.get(
        `/api/player/lessons?id=${payload.id}&timeZone=${time}`
      );

      return { ...data, idSA: payload.idSA };
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchInsightsQuestions = createAsyncThunk(
  'gePlayerInsights/fetchInsightsQuestions',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('api/insight-questions');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const saveInsightsQuestions = createAsyncThunk(
  'gePlayerInsights/saveInsightsQuestions',
  async (
    payload: { answers: GEQuestions[]; sessionId: number },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post('api/insight-questions/answers', {
        questions: payload.answers,
        sessionId: payload.sessionId,
      });
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const saveInsightsChooseType = createAsyncThunk(
  'gePlayerInsights/saveInsightsChooseType',
  async (
    payload: {
      group: string;
      id?: number;
      isSkillAssessment?: boolean;
      lessonType?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post('api/player-insight/init-answer', {
        group: payload.group,
        ...(payload?.id && { id: payload.id }),
        ...(payload?.isSkillAssessment && {
          isSkillAssessment: payload.isSkillAssessment,
        }),
        ...(payload?.lessonType && {
          lessonType: payload.lessonType,
        }),
      });
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const initPaymentPlan = createAsyncThunk(
  'gePlayerInsights/initPaymentPlan',
  async (geId: number, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/api/game-evaluation/init-payment?geId=${geId}`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getStatusPaymentPlan = createAsyncThunk(
  'gePlayerInsights/initPaymentPlan',
  async (uuid: string, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/api/game-evaluation/payment-status?uuid=${uuid}`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchRivFiles = createAsyncThunk(
  'gePlayerInsights/fetchRivFiles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`/api/player-insight/riv-files`);

      return { ...data };
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

const initialState: GEPlayerInsightsState = {
  openGE: false,
  statusStepWrapper: 'start',
  questions: 0,
  dataInsightQuestion: null,
  loadingDataInsightQuestion: 'unload',
  playerInsights: null,
  dataSkillAssessment: null,
  arrayDetailSA: [],
  idSADetail: -1,
  loadingDetailSA: false,
  loadingDataSkillAssessment: 'unload',
  typeDataSkillAssessment: '',
  dataSkillAssessmentDetail: null,
  loadingDetailLesson: false,
  stationGESelected: null,
  playerInsightSelected: null,
  playerInsightsEmbed: null,
  questionsNotSendServer: [],
  errorFetchData: 0,
  loadingDataPlayerInsight: 'unload',
};

export const gePlayerInsightsSlice = createSlice({
  name: 'gePlayerInsights',
  initialState,
  reducers: {
    changeStatusOpenGE: (state, action) => {
      state.openGE = action.payload;
    },
    changeStatusStepWrapper: (state, action) => {
      state.statusStepWrapper = action.payload;
    },
    changeErrorFetchData: (state, action) => {
      state.errorFetchData = action.payload;
    },
    mergeQuestionPlayerInsight: (state, action) => {
      state.playerInsightSelected = action.payload;
      if (action.payload?.sessionId) {
        if (action.payload?.sessionId === state.playerInsights?.sessionId) {
          state.playerInsights = action.payload;
        }
        const findIndexGE = state.arrayDetailSA.findIndex(
          (i) => i?.playerInsightById?.sessionId === action.payload.sessionId
        );
        if (findIndexGE !== -1) {
          state.arrayDetailSA[findIndexGE] = {
            ...state.arrayDetailSA[findIndexGE],
            playerInsightById: action.payload,
          };
        }
      }
    },
    changeNumberQuestions: (state, action) => {
      state.questions = action.payload;
    },
    changeIdSADetail: (state, action) => {
      state.idSADetail = action.payload;
      if (state.errorFetchData !== action.payload) {
        state.errorFetchData = 0;
      }
    },
    changeDetailAssessment: (state, action) => {
      state.dataSkillAssessmentDetail = action.payload;
      state.typeDataSkillAssessment = action.payload.type;
      state.idSADetail = action.payload.id;
      state.stationGESelected =
        action.payload?.skillAssessment?.stations?.[0] || null;
    },
    changeTypeSA: (state, action) => {
      state.typeDataSkillAssessment = action.payload;
    },
    changeStationSA: (state, action) => {
      state.stationGESelected = action.payload;
    },

    changePlayerInsight: (state, action) => {
      state.playerInsights = action.payload;
    },
    mergeQuestionPlayerInsighSelected: (state, action) => {
      state.playerInsightSelected = action.payload;
    },
    changeQuestionsNotSendServer: (state, action) => {
      state.questionsNotSendServer = action.payload;
    },
    mergeQuestionPlayerInsighEmbed: (state, action) => {
      state.playerInsightsEmbed = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInsightsQuestions.pending, (state, action) => {
        state.loadingDataInsightQuestion = 'loading';
      })
      .addCase(fetchInsightsQuestions.fulfilled, (state, action) => {
        state.dataInsightQuestion = action.payload.data;
        state.loadingDataInsightQuestion = 'loaded';
      })

      .addCase(fetchInsightsQuestions.rejected, (state, action) => {
        state.loadingDataInsightQuestion = 'loaded';
      })
      .addCase(fetchPlayerInsight.pending, (state, action) => {
        state.loadingDataPlayerInsight = 'loading';
      })
      .addCase(fetchPlayerInsight.fulfilled, (state, action) => {
        state.playerInsights = action.payload.data;
        state.playerInsightSelected = action.payload.data;
        state.loadingDataPlayerInsight = 'loaded';
      })
      .addCase(fetchPlayerInsight.rejected, (state, action) => {
        state.loadingDataPlayerInsight = 'loaded';
      })
      .addCase(fetchPlayerInsightById.fulfilled, (state, action) => {
        if (state.dataSkillAssessmentDetail && action.payload.data) {
          state.dataSkillAssessmentDetail = {
            ...state.dataSkillAssessmentDetail,
            playerInsightById: action.payload.data,
          };
          const idx = state.arrayDetailSA.findIndex(
            (i) => i.id === action.payload?.idSA
          );
          if (idx >= 0) {
            state.arrayDetailSA[idx] = {
              ...state.arrayDetailSA[idx],
              playerInsightById: action.payload.data,
            };
          }
        }
      })
      .addCase(fetchGE.pending, (state, action) => {
        state.loadingDataSkillAssessment = 'loading';
      })
      .addCase(fetchGE.fulfilled, (state, action) => {
        if (action.payload.data && action.payload.data?.length) {
          state.dataSkillAssessment = [...action.payload.data].reverse();
          // state.dataSkillAssessment = action.payload.data;
        }
        state.loadingDataSkillAssessment = 'loaded';
      })

      .addCase(fetchGE.rejected, (state, action) => {
        state.loadingDataSkillAssessment = 'loaded';
      })
      .addCase(fetchGEById.pending, (state, action) => {
        state.loadingDetailSA = true;
      })
      .addCase(fetchGERecent.fulfilled, (state, action) => {
        if (action.payload.data?.id) {
          const idx = state.arrayDetailSA.findIndex(
            (i) => i.id === action.payload.data.id
          );
          if (idx >= 0) {
            const oldItem = state.arrayDetailSA[idx];

            state.arrayDetailSA[idx] = {
              ...action.payload.data,
              lessonById: oldItem.lessonById,
              playerInsightById: oldItem.playerInsightById,
              lastSA: true,
            };
            if (state.idSADetail === -1) {
              state.dataSkillAssessmentDetail = {
                ...action.payload.data,
                lessonById: oldItem.lessonById,
                playerInsightById: oldItem.playerInsightById,
                lastSA: true,
              };
            }
          } else {
            state.arrayDetailSA.push({ ...action.payload.data, lastSA: true });
            if (state.idSADetail === -1) {
              state.dataSkillAssessmentDetail = {
                ...action.payload.data,
                lastSA: true,
              };
            }
          }
          if (state?.dataSkillAssessment?.length) {
            const idex = state.dataSkillAssessment?.findIndex(
              (i) => i.id === action.payload.data.id
            );
            if (idex >= 0) {
              const oldItem = state.dataSkillAssessment[idex];

              state.dataSkillAssessment[idex] = {
                ...action.payload.data,
                lessonById: oldItem.lessonById,
                playerInsightById: oldItem.playerInsightById,
                lastSA: true,
              };
            } else {
              state.dataSkillAssessment.push(action.payload.data);
            }
          }
          if (state.idSADetail === -1) {
            state.idSADetail = action.payload.data?.id;
            state.typeDataSkillAssessment = action.payload.data.type;
            if (action.payload.data?.skillAssessment?.stations?.length) {
              state.stationGESelected =
                action.payload.data?.skillAssessment?.stations?.[0];
            }
          }
        }
      })
      .addCase(fetchGEById.fulfilled, (state, action) => {
        if (action.payload.data?.id) {
          const idx = state.arrayDetailSA.findIndex(
            (i) => i.id === action.payload.data.id
          );
          if (idx >= 0) {
            const oldItem = state.arrayDetailSA[idx];

            state.arrayDetailSA[idx] = {
              ...action.payload.data,
              lessonById: oldItem.lessonById,
              playerInsightById: oldItem.playerInsightById,
              lastSA: oldItem.lastSA,
            };
            state.dataSkillAssessmentDetail = {
              ...action.payload.data,
              lessonById: oldItem.lessonById,
              playerInsightById: oldItem.playerInsightById,
              lastSA: oldItem.lastSA,
            };
          } else {
            state.arrayDetailSA.push(action.payload.data);
            state.dataSkillAssessmentDetail = action.payload.data;
          }
          if (state?.dataSkillAssessment?.length) {
            const idex = state.dataSkillAssessment?.findIndex(
              (i) => i.id === action.payload.data.id
            );
            if (idex >= 0) {
              const oldItem = state.dataSkillAssessment[idex];

              state.dataSkillAssessment[idex] = {
                ...action.payload.data,
                lessonById: oldItem.lessonById,
                playerInsightById: oldItem.playerInsightById,
                lastSA: true,
              };
            } else {
              state.dataSkillAssessment.push(action.payload.data);
            }
          }
          state.idSADetail = action.payload.data?.id;
          state.typeDataSkillAssessment = action.payload.data.type;
          if (action.payload.data?.skillAssessment?.stations?.length) {
            state.stationGESelected =
              action.payload.data?.skillAssessment?.stations?.[0];
          }
        }
        state.loadingDetailSA = false;
      })
      .addCase(fetchPlayerInsightBySessionId.fulfilled, (state, action) => {
        state.playerInsightsEmbed = action.payload.data;
      })
      .addCase(fetchGEByIdNoneLoading.fulfilled, (state, action) => {
        if (action.payload.data?.id) {
          const idx = state.arrayDetailSA.findIndex(
            (i) => i.id === action.payload.data.id
          );
          if (idx >= 0) {
            const oldItem = state.arrayDetailSA[idx];

            state.arrayDetailSA[idx] = {
              ...action.payload.data,
              lessonById: oldItem.lessonById,
              playerInsightById: oldItem.playerInsightById,
              lastSA: oldItem.lastSA,
            };
            state.dataSkillAssessmentDetail = {
              ...action.payload.data,
              lessonById: oldItem.lessonById,
              playerInsightById: oldItem.playerInsightById,
              lastSA: oldItem.lastSA,
            };
          } else {
            state.arrayDetailSA.push(action.payload.data);
            state.dataSkillAssessmentDetail = action.payload.data;
          }
          if (state?.dataSkillAssessment?.length) {
            const idex = state.dataSkillAssessment?.findIndex(
              (i) => i.id === action.payload.data.id
            );
            if (idex >= 0) {
              const oldItem = state.dataSkillAssessment[idex];

              state.dataSkillAssessment[idex] = {
                ...action.payload.data,
                lessonById: oldItem.lessonById,
                playerInsightById: oldItem.playerInsightById,
                lastSA: true,
              };
            } else {
              state.dataSkillAssessment.push(action.payload.data);
            }
          }
          state.idSADetail = action.payload.data?.id;
          state.typeDataSkillAssessment = action.payload.data.type;
          if (action.payload.data?.skillAssessment?.stations?.length) {
            state.stationGESelected =
              action.payload.data?.skillAssessment?.stations?.[0];
          }
        }
        state.loadingDetailSA = false;
      })
      .addCase(fetchGEById.rejected, (state, action) => {
        state.loadingDetailSA = false;
        state.dataSkillAssessmentDetail = null;
      })
      .addCase(fetchSALessonById.fulfilled, (state, action) => {
        if (
          state.dataSkillAssessmentDetail &&
          action.payload.data &&
          action.payload.code !== 30001
        ) {
          state.dataSkillAssessmentDetail = {
            ...state.dataSkillAssessmentDetail,
            lessonById: action.payload.data,
          };
          const idx = state.arrayDetailSA.findIndex(
            (i) => i.id === action.payload?.idSA
          );
          if (idx >= 0) {
            state.arrayDetailSA[idx] = {
              ...state.arrayDetailSA[idx],
              lessonById: action.payload.data,
            };
          }
        }
      });
    builder.addCase(signOut, () => ({ ...initialState }));
  },
});

export const {
  changeStatusOpenGE,
  changeStatusStepWrapper,
  changeNumberQuestions,
  mergeQuestionPlayerInsight,
  changeIdSADetail,
  changeTypeSA,
  changeDetailAssessment,
  changeStationSA,
  changePlayerInsight,
  mergeQuestionPlayerInsighSelected,
  changeQuestionsNotSendServer,
  mergeQuestionPlayerInsighEmbed,
  changeErrorFetchData,
} = gePlayerInsightsSlice.actions;

export const selectStatusOpenGE = (state: RootState) =>
  state.gePlayerInsight.openGE;
export const selectStatusStepWrapper = (state: RootState) =>
  state.gePlayerInsight.statusStepWrapper;
export const selectPlayerInsightsRecent = (state: RootState) =>
  state.gePlayerInsight.playerInsights;
export const selectPlayerInsights = (state: RootState) =>
  state.gePlayerInsight.playerInsightSelected;
export const selectPlayerInsightsNumberQuestions = (state: RootState) =>
  state.gePlayerInsight.questions;
export const selectDataInsightQuestions = (state: RootState) =>
  state.gePlayerInsight.dataInsightQuestion;
export const selectDataGESkillAssessment = (state: RootState) =>
  state.gePlayerInsight.dataSkillAssessment;
export const selectLoadingDataGESkillAssessment = (state: RootState) =>
  state.gePlayerInsight.loadingDataSkillAssessment;
export const selectIdSADetail = (state: RootState) =>
  state.gePlayerInsight.idSADetail;
export const selectTypeSA = (state: RootState) =>
  state.gePlayerInsight.typeDataSkillAssessment;
export const selectDetailSA = (state: RootState) =>
  state.gePlayerInsight.dataSkillAssessmentDetail;
export const selectLoadingDetailSA = (state: RootState) =>
  state.gePlayerInsight.loadingDetailSA;
export const selectArrayDetailSA = (state: RootState) =>
  state.gePlayerInsight.arrayDetailSA;
export const selectDataGEStationSelected = (state: RootState) =>
  state.gePlayerInsight.stationGESelected;
export const selectLoadingDataInsightQuestions = (state: RootState) =>
  state.gePlayerInsight.loadingDataInsightQuestion;
export const selectPlayerInsightEmbed = (state: RootState) =>
  state.gePlayerInsight.playerInsightsEmbed;
export const selectQuestionsNotSendServer = (state: RootState) =>
  state.gePlayerInsight.questionsNotSendServer;
export const selectErrorFetchDataGE = (state: RootState) =>
  state.gePlayerInsight.errorFetchData;
export const selectLoadingPlayerInsightRecent = (state: RootState) =>
  state.gePlayerInsight.loadingDataPlayerInsight;

export default gePlayerInsightsSlice.reducer;
